import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  PaidOutlined as PaidIcon,
  StorefrontOutlined as StoreIcon,
  ConfirmationNumberOutlined as PassIcon,
  AddShoppingCartOutlined as BuyPassIcon,
  CheckCircleOutlined as SuccessIcon,
} from "@mui/icons-material";
import LessonList from "../components/payments/LessonList";
import PassCard from "../components/payments/PassCard";
import { plural, formatDate, formatPln } from "../utils/paymentFormat";
import "../assets/style/PayForLesson.css";

// Public landing page reached from the "Zapłać online" link in the
// admin-driven UNPAID-signup reminder email (and from the in-app banner). NO
// Firebase auth — the URL token is the proof of intent, validated server-side.
//
// Aggregated settlement: the token resolves to the recipient, and ALL of their
// still-unpaid admin-signup lessons are shown together. The user can either
//   - pay the WHOLE balance online via PayU (one redirect), or
//   - apply one of their existing passes (covers as many lessons as it can).
//
// States: loading → loaded (list + actions) → error (404 expired / 410 all
// paid) → done (everything settled in-session via a pass).

const API = process.env.REACT_APP_BACKEND_ADDRESS;

const PayForLesson = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [passes, setPasses] = useState([]);
  const [purchasablePasses, setPurchasablePasses] = useState([]);
  const [error, setError] = useState(null); // { code, message }
  const [submitting, setSubmitting] = useState(false);
  const [usingPassId, setUsingPassId] = useState(null);
  const [buyingPassName, setBuyingPassName] = useState(null);
  const [notice, setNotice] = useState(null); // transient success after partial pass settle
  const [done, setDone] = useState(null); // final success message (all settled)

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const infoResp = await axios.get(`${API}/pay-for-lesson/${token}`);
      setInfo(infoResp.data);
      try {
        const passResp = await axios.get(
          `${API}/pay-for-lesson/${token}/passes`
        );
        setPasses(Array.isArray(passResp.data) ? passResp.data : []);
      } catch {
        // Passes are an optional convenience — a failure here must not block
        // the online-payment path.
        setPasses([]);
      }
      try {
        const buyResp = await axios.get(
          `${API}/pay-for-lesson/${token}/purchasable-passes`
        );
        setPurchasablePasses(
          Array.isArray(buyResp.data) ? buyResp.data : []
        );
      } catch {
        // Buyable passes are an optional convenience too — never block payment.
        setPurchasablePasses([]);
      }
    } catch (e) {
      const status = e?.response?.status;
      let message = "Nie udało się pobrać informacji o płatności.";
      if (status === 404) {
        message =
          "Link jest nieaktualny lub został już użyty. Skontaktuj się z instruktorem.";
      } else if (status === 410) {
        message = "Wszystkie zajęcia są już opłacone. Dziękujemy!";
      } else if (e?.response?.data?.detail) {
        message = e.response.data.detail;
      }
      setError({ code: status, message });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Once everything is settled this page has nothing left to do, so send the
  // user back to the main site. The confirmation shows as a dialog in the
  // meantime; we also auto-redirect after a few seconds in case they don't tap
  // the button.
  useEffect(() => {
    if (!done) return undefined;
    const timer = setTimeout(() => navigate("/"), 5000);
    return () => clearTimeout(timer);
  }, [done, navigate]);

  const handlePayAll = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      const resp = await axios.post(`${API}/pay-for-lesson/${token}`);
      const redirect = resp?.data?.redirect_url;
      if (redirect) {
        window.location.href = redirect;
      } else {
        setError({ code: 500, message: "PayU nie zwrócił adresu płatności." });
        setSubmitting(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError({
        code: e?.response?.status,
        message: `Nie udało się rozpocząć płatności: ${msg}`,
      });
      setSubmitting(false);
    }
  };

  const handleUsePass = async (passId) => {
    setUsingPassId(passId);
    setNotice(null);
    try {
      const resp = await axios.post(`${API}/pay-for-lesson/${token}/use-pass`, {
        pass_id: passId,
      });
      const settled = resp?.data?.settled_count || 0;
      const remaining = resp?.data?.remaining_count || 0;
      const settledLabel = `Karnet pokrył ${settled} ${plural(
        settled,
        "termin",
        "terminy",
        "terminów"
      )}`;
      if (remaining === 0) {
        setDone({
          message: `${settledLabel}. Wszystko uregulowane — dziękujemy!`,
        });
      } else {
        setNotice(
          `${settledLabel}. Pozostało jeszcze ${remaining} ${plural(
            remaining,
            "termin",
            "terminy",
            "terminów"
          )} do opłacenia.`
        );
        await loadAll();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError({
        code: e?.response?.status,
        message: `Nie udało się użyć karnetu: ${msg}`,
      });
    } finally {
      setUsingPassId(null);
    }
  };

  const handleBuyPass = async (passTypeName) => {
    setBuyingPassName(passTypeName);
    setNotice(null);
    try {
      const resp = await axios.post(
        `${API}/pay-for-lesson/${token}/buy-pass`,
        { pass_type_name: passTypeName }
      );
      const redirect = resp?.data?.redirect_url;
      if (redirect) {
        window.location.href = redirect;
      } else {
        setError({ code: 500, message: "PayU nie zwrócił adresu płatności." });
        setBuyingPassName(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError({
        code: e?.response?.status,
        message: `Nie udało się rozpocząć zakupu karnetu: ${msg}`,
      });
      setBuyingPassName(null);
    }
  };

  const lessons = info?.lessons || [];
  const count = info?.count || lessons.length;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
      <Paper elevation={3} className="pay-for-lesson__paper">
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            nat•chnienie studio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Uregulowanie opłaty za zajęcia
          </Typography>
        </Stack>

        <Box sx={{ mt: 3 }}>
          {loading && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          )}

          {!loading && !done && error && (
            <Alert severity={error.code === 410 ? "success" : "warning"}>
              {error.message}
            </Alert>
          )}

          {!loading && !done && !error && info && (
            <Stack spacing={2.5}>
              <Typography variant="body1">
                Cześć <b>{info.user_first_name}</b>! Instruktor zapisał Cię na
                poniższe zajęcia. Możesz opłacić je wszystkie naraz.
              </Typography>

              {notice && <Alert severity="info">{notice}</Alert>}

              <LessonList
                lessons={lessons}
                totalLabel={`Razem (${count} ${plural(
                  count,
                  "termin",
                  "terminy",
                  "terminów"
                )})`}
                totalAmount={info.total_amount}
              />

              {info.location_name && (
                <Typography variant="body2" color="text.secondary">
                  Lokalizacja: {info.location_name}
                  {info.location_address ? ` — ${info.location_address}` : ""}
                </Typography>
              )}

              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handlePayAll}
                startIcon={
                  submitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <PaidIcon />
                  )
                }
                disabled={submitting || usingPassId !== null || buyingPassName !== null}
                className="pay-for-lesson__cta"
              >
                Zapłać wszystko przez PayU ({formatPln(info.total_amount)})
              </Button>

              {passes.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 2 }}>
                    <Chip
                      icon={<PassIcon />}
                      label="lub użyj karnetu"
                      size="small"
                    />
                  </Divider>
                  <Stack spacing={1.5}>
                    {passes.map((p) => (
                      <PassCard
                        key={p.pass_id}
                        title={p.pass_type_name}
                        caption={
                          <>
                            Ważny do {formatDate(p.valid_until)} · pokryje{" "}
                            {p.covers_count} z {count}
                          </>
                        }
                        warning={
                          p.covers_count < count ? (
                            <>
                              Nie obejmie wszystkich terminów. Pozostałe{" "}
                              {count - p.covers_count}{" "}
                              {plural(
                                count - p.covers_count,
                                "termin",
                                "terminy",
                                "terminów"
                              )}{" "}
                              opłać osobno — np. gotówką na miejscu.
                            </>
                          ) : null
                        }
                        actionLabel="Użyj karnetu"
                        onAction={() => handleUsePass(p.pass_id)}
                        disabled={
                          submitting ||
                          usingPassId !== null ||
                          buyingPassName !== null
                        }
                        loading={usingPassId === p.pass_id}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {purchasablePasses.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 2 }}>
                    <Chip
                      icon={<BuyPassIcon />}
                      label="lub kup karnet"
                      size="small"
                    />
                  </Divider>
                  <Stack spacing={1.5}>
                    {purchasablePasses.map((p) => (
                      <PassCard
                        key={p.pass_type_name}
                        title={p.pass_type_name}
                        caption={
                          <>
                            {formatPln(p.price)} · pokryje {p.covers_count} z{" "}
                            {count}
                            {p.valid_until_preview
                              ? ` · ważny do ${formatDate(p.valid_until_preview)}`
                              : p.valid_days
                              ? ` · ważny ${p.valid_days} ${plural(
                                  p.valid_days,
                                  "dzień",
                                  "dni",
                                  "dni"
                                )}`
                              : ""}
                          </>
                        }
                        warning={
                          p.covers_count < count ? (
                            <>
                              Nie obejmie wszystkich terminów (limit wejść lub
                              data ważności). Pozostałe {count - p.covers_count}{" "}
                              {plural(
                                count - p.covers_count,
                                "termin",
                                "terminy",
                                "terminów"
                              )}{" "}
                              opłać osobno (np. gotówką na miejscu) lub wybierz
                              inny karnet.
                            </>
                          ) : null
                        }
                        actionLabel="Kup karnet"
                        onAction={() => handleBuyPass(p.pass_type_name)}
                        disabled={
                          submitting ||
                          usingPassId !== null ||
                          buyingPassName !== null
                        }
                        loading={buyingPassName === p.pass_type_name}
                        buttonVariant="contained"
                        startIcon={<BuyPassIcon />}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Box className="pay-for-lesson__cash">
                <StoreIcon fontSize="small" color="action" sx={{ mt: "2px" }} />
                <Typography variant="body2" color="text.secondary">
                  Możesz też zapłacić gotówką na miejscu — u instruktora przed
                  lub po zajęciach.
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>

      <Dialog
        open={Boolean(done)}
        onClose={() => navigate("/")}
        aria-labelledby="pay-for-lesson-done"
      >
        <DialogContent>
          <Stack
            spacing={2}
            alignItems="center"
            textAlign="center"
            sx={{ py: 2 }}
          >
            <SuccessIcon color="success" sx={{ fontSize: 56 }} />
            <Typography
              id="pay-for-lesson-done"
              variant="h6"
              sx={{ fontWeight: 700 }}
            >
              {done?.message}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
          >
            Przejdź do strony głównej
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PayForLesson;
